import { baseUrl } from './api.js';

/**
 * Krok POB1 — pobočky kanceláře (`/v1/offices`). Stejný vzor jako
 * `assistant-settings-api.ts`: raw `fetch`, protože routy jsou nové a ještě
 * nejsou v generovaném `@reos/api-client` schématu.
 *
 * Chyba serveru se nese i s problem+json `detail` (archivace pobočky
 * s neuzavřenými zakázkami vrací 409 a počet zakázek je právě v `detail`) —
 * bez něj by obrazovka hlásila jen „HTTP 409" a makléř nevěděl proč.
 */

export interface OfficeDto {
  readonly id: string;
  readonly name: string;
  readonly locale: string;
  readonly addressText: string | null;
  readonly addressLat: string | null;
  readonly addressLon: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly ico: string | null;
  readonly establishmentId: string | null;
  readonly invoiceSeries: string | null;
  readonly archivedAt: string | null;
  readonly createdAt: string;
  readonly memberUserIds: readonly string[];
  readonly openCaseCount: number;
}

export interface OfficeInput {
  readonly name?: string;
  readonly addressText?: string | null;
  readonly addressLat?: string | null;
  readonly addressLon?: string | null;
  readonly email?: string | null;
  readonly phone?: string | null;
  readonly ico?: string | null;
  readonly establishmentId?: string | null;
  readonly invoiceSeries?: string | null;
}

interface ProblemBody {
  readonly detail?: string;
  readonly title?: string;
}

async function parse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    let detail = '';
    try {
      const body = (await res.json()) as ProblemBody;
      detail = body.detail ?? body.title ?? '';
    } catch {
      detail = '';
    }
    throw new Error(detail === '' ? `HTTP ${String(res.status)}` : detail);
  }
  return (await res.json()) as T;
}

/**
 * Krok CEN2 — makléři v tarifu kanceláře (`GET /v1/crm/office-seats`).
 * Čísla počítá server z CENÍKU a ze členů kanceláře (`officeSeatUsage`);
 * obrazovka Tým je jen vypisuje, aby nemohla říct jiný počet, než kolik se
 * doúčtuje.
 */
export interface OfficeSeatsDto {
  readonly planCode: string | null;
  readonly includedUsers: number | null;
  readonly activeUsers: number;
  readonly extraUsers: number;
  readonly extraUserPriceNet: string | null;
  readonly extraUserPriceGross: string | null;
  readonly currency: string | null;
}

export async function getOfficeSeats(): Promise<OfficeSeatsDto> {
  const res = await fetch(`${baseUrl}/v1/crm/office-seats`, { credentials: 'include' });
  return parse<OfficeSeatsDto>(res);
}

export async function listOffices(includeArchived = false): Promise<readonly OfficeDto[]> {
  const query = includeArchived ? '?includeArchived=true' : '';
  const res = await fetch(`${baseUrl}/v1/offices${query}`, { credentials: 'include' });
  const body = await parse<{ items: readonly OfficeDto[] }>(res);
  return body.items;
}

export async function createOffice(input: OfficeInput & { readonly name: string }): Promise<OfficeDto> {
  const res = await fetch(`${baseUrl}/v1/offices`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  return parse<OfficeDto>(res);
}

export async function updateOffice(id: string, input: OfficeInput): Promise<OfficeDto> {
  const res = await fetch(`${baseUrl}/v1/offices/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(input),
  });
  return parse<OfficeDto>(res);
}

export async function setOfficeMembers(id: string, userIds: readonly string[]): Promise<OfficeDto> {
  const res = await fetch(`${baseUrl}/v1/offices/${encodeURIComponent(id)}/members`, {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ userIds }),
  });
  return parse<OfficeDto>(res);
}

export async function setOfficeArchived(id: string, archived: boolean): Promise<OfficeDto> {
  const res = await fetch(`${baseUrl}/v1/offices/${encodeURIComponent(id)}/archive`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ archived }),
  });
  return parse<OfficeDto>(res);
}
